/**
 * Service Worker 수동 등록 + 업데이트 감지.
 *
 * vite-plugin-pwa의 injectRegister: false 설정과 함께 사용한다.
 * SSR/CSR 모두에서 entry-client.tsx가 호출하지만, 실제 등록은 브라우저에서만 수행한다.
 *
 * 업데이트 감지 시 'pwa:update-available' 커스텀 이벤트를 발행하여
 * UpdateToast 컴포넌트가 사용자에게 알린다.
 *
 * 영구지침: docs/develop/pwa/pwa-spec.md §3.3
 */

const SW_URL = '/sw.js'
const UPDATE_AVAILABLE_EVENT = 'pwa:update-available'

let registration: ServiceWorkerRegistration | null = null

export async function registerServiceWorker(): Promise<void> {
  if (typeof window === 'undefined') return
  if (!('serviceWorker' in navigator)) return

  // dev 모드에서도 SW 등록 시도 — vite-plugin-pwa의 devOptions.enabled: true와 쌍으로 동작.
  // vite-plugin-pwa는 dev 모드에서 /dev-sw.js?dev-sw를 서빙함.
  const swUrl = import.meta.env.DEV ? '/dev-sw.js?dev-sw' : SW_URL
  const swOptions: RegistrationOptions = import.meta.env.DEV
    ? { scope: '/', type: 'module' }
    : { scope: '/' }

  // 등록 전에 기억: 없었다면 이후 첫 controllerchange는 sw.ts의 clients.claim()일 뿐(새로고침 불필요)
  const hadController = !!navigator.serviceWorker.controller

  try {
    registration = await navigator.serviceWorker.register(swUrl, swOptions)

    // 업데이트 감지: 새 SW가 install됨 → 'pwa:update-available' 이벤트
    registration.addEventListener('updatefound', () => {
      const newWorker = registration?.installing
      if (!newWorker) return

      newWorker.addEventListener('statechange', () => {
        if (
          newWorker.state === 'installed' &&
          navigator.serviceWorker.controller
        ) {
          // 기존 SW가 있는 상태에서 새 SW가 설치 완료 → 업데이트 가능
          window.dispatchEvent(new CustomEvent(UPDATE_AVAILABLE_EVENT))
        }
      })
    })

    navigator.serviceWorker.addEventListener(
      'controllerchange',
      createControllerChangeHandler(hadController, () => window.location.reload())
    )
  } catch (error) {
    // SW 등록 실패는 사용자 경험에 영향 없음
    console.warn('[PWA] Service Worker registration failed:', error)
  }
}

/**
 * controllerchange 처리기 — 기존 SW → 새 SW 교체일 때만 1회 reload (pwa-spec §3.3).
 * 첫 설치(claim)는 건너뛰되, 그 뒤 같은 페이지에서 또 바뀌면 배포로 인한 교체이므로 reload한다.
 */
export function createControllerChangeHandler(
  hadController: boolean,
  reload: () => void
): () => void {
  let controlled = hadController
  let refreshing = false
  return () => {
    if (!controlled) {
      controlled = true
      return
    }
    if (refreshing) return
    refreshing = true
    reload()
  }
}

/** 새 SW에 SKIP_WAITING 메시지 전송 → controllerchange → 페이지 reload */
export function applyUpdate(): void {
  registration?.waiting?.postMessage({ type: 'SKIP_WAITING' })
}
